import { Widget } from '../core/Widget.js';
import { isWidgetPendingChanges } from '../core/WidgetPendingChanges.js';

// view/BgSaveScope.ts
/*
 * 2026-10-15: область фонового сохранения формы ([data-bgsave-scope]).
 *
 * Внутри области все itemcmd (bgSave, onchange-команды полей, кнопки) идут
 * в одну последовательную очередь. Перед завершением (кнопка с data-wait-bgsave,
 * закрытие aside) область:
 *   1) переходит в finishing — повторное завершение игнорируется;
 *   2) просит виджеты отдать отложенные изменения (WidgetPendingChanges);
 *   3) явно снимает фокус с активного элемента области (change при blur);
 *   4) повторно собирает отложенные изменения (blur редактора);
 *   5) повторяет неуспешные операции, помеченные как повторяемые;
 *   6) блокируется: inert и aria-busy (индикатор занятости view не включается);
 *   7) дожидается стабильно пустой очереди с учётом поколений.
 * Итог операции фиксируется по ключу (view, itemKey): последующий успех того же
 * поля снимает прежнюю ошибку, а ошибки отключённых после refresh view не учитываются.
 *
 * 2026-10-15: исключение любого flushPendingChanges делает завершение неуспешным:
 * отложенное изменение могло не попасть в очередь. Повторяемая операция (сохранение
 * значения поля) при ошибке запоминается и отправляется заново при следующем завершении,
 * без искусственного изменения значения: CKEditor не шлёт change повторно для того же
 * содержимого. Наличие успешных операций (hasWrites) определяет выход из aside:
 * с записями — серверная команда завершения, без записей — простое закрытие.
 *
 * 2026-10-15: признак неуспешного последнего завершения (hasFailedFinish) снимается
 * только успешным завершением. По нему повторное закрытие aside после ошибки
 * предлагает закрыть панель без сохранения.
 */
/** Селектор области фонового сохранения */
const BGSAVE_SCOPE_SELECTOR = "[data-bgsave-scope]";
const states = new WeakMap();
function stateOf(scope) {
    let st = states.get(scope);
    if (!st) {
        st = {
            tail: Promise.resolve(),
            pending: 0,
            generation: 0,
            results: new Map(),
            writes: 0,
            finishing: false,
            failedFinish: false
        };
        states.set(scope, st);
    }
    return st;
}
/** Следующая macrotask: даёт применить ответ и выполнить порождённые им change */
function nextTask() {
    return new Promise(resolve => setTimeout(resolve, 0));
}
class BgSaveScope {
    /** Ближайшая область, содержащая элемент */
    static closest(el) {
        return el?.closest(BGSAVE_SCOPE_SELECTOR) ?? null;
    }
    /** Область на самом элементе или внутри него (например, внутри aside) */
    static within(root) {
        return root.matches(BGSAVE_SCOPE_SELECTOR)
            ? root
            : root.querySelector(BGSAVE_SCOPE_SELECTOR);
    }
    /** Идёт ли завершение области */
    static isFinishing(scope) {
        return states.get(scope)?.finishing === true;
    }
    /**
     * Ставит операцию в последовательную очередь области.
     * Регистрация синхронная: операция учитывается ожиданием сразу после вызова.
     * @param viewEl  элемент view, отправляющего команду
     * @param itemKey ключ элемента (поле или кнопка)
     * @param run     выполнение операции; true — успех
     * @param retryable операцию можно безопасно повторить (сохранение значения поля):
     *                  после ошибки она отправляется заново при следующем finish
     */
    static enqueue(scope, viewEl, itemKey, run, retryable = false) {
        const st = stateOf(scope);
        st.pending++;
        st.generation++;
        const op = st.tail.then(async () => {
            let ok = false;
            try {
                ok = await run();
            }
            catch (e) {
                console.error("[BgSaveScope] operation failed", e);
            }
            finally {
                let byView = st.results.get(viewEl);
                if (!byView) {
                    byView = new Map();
                    st.results.set(viewEl, byView);
                }
                byView.set(itemKey ?? "", ok
                    ? { result: "success" }
                    : { result: "error", retry: retryable ? run : undefined });
                if (ok)
                    st.writes++;
                st.pending--;
            }
        });
        st.tail = op;
        return op;
    }
    /** Было ли последнее завершение области неуспешным */
    static hasFailedFinish(scope) {
        return states.get(scope)?.failedFinish === true;
    }
    /** Были ли в области успешные операции (записи) */
    static hasWrites(scope) {
        return (states.get(scope)?.writes ?? 0) > 0;
    }
    /** Итоги операций view, ещё находящихся в области; итоги отключённых view отбрасываются */
    static liveResults(scope, st) {
        const live = [];
        for (const [viewEl, byView] of Array.from(st.results)) {
            if (!viewEl.isConnected || !scope.contains(viewEl)) {
                st.results.delete(viewEl);
                continue;
            }
            live.push([viewEl, byView]);
        }
        return live;
    }
    /** Есть ли ошибки операций у view, ещё находящихся в области */
    static hasErrors(scope) {
        const st = states.get(scope);
        if (!st)
            return false;
        return BgSaveScope.liveResults(scope, st)
            .some(([, byView]) => Array.from(byView.values()).some(e => e.result === "error"));
    }
    /**
     * Ставит в очередь повтор неуспешных повторяемых операций.
     * @returns число поставленных повторов
     */
    static retryFailed(scope) {
        const st = states.get(scope);
        if (!st)
            return 0;
        let count = 0;
        for (const [viewEl, byView] of BgSaveScope.liveResults(scope, st)) {
            for (const [itemKey, entry] of Array.from(byView)) {
                if (entry.result !== "error" || !entry.retry)
                    continue;
                BgSaveScope.enqueue(scope, viewEl, itemKey, entry.retry, true);
                count++;
            }
        }
        return count;
    }
    /**
     * Дожидается стабильно пустой очереди: после завершения хвоста за одну macrotask
     * не появилось новых операций.
     * @returns true, если ошибок нет
     */
    static async waitIdle(scope) {
        const st = stateOf(scope);
        for (;;) {
            const generation = st.generation;
            await st.tail;
            await nextTask();
            if (st.pending === 0 && st.generation === generation)
                break;
        }
        return !BgSaveScope.hasErrors(scope);
    }
    /**
     * Просит виджеты области синхронно отправить отложенные изменения.
     * Исключение одного виджета не мешает остальным.
     * @returns false, если хотя бы один виджет завершился исключением
     */
    static flushPendingChanges(scope) {
        let ok = true;
        for (const w of Widget.getAll()) {
            const el = w.element;
            if (!el || !scope.contains(el) || !isWidgetPendingChanges(w))
                continue;
            try {
                w.flushPendingChanges();
            }
            catch (e) {
                ok = false;
                console.error("[BgSaveScope] flushPendingChanges failed", e);
            }
        }
        return ok;
    }
    /**
     * Завершение области: собирает последние изменения, блокирует область
     * и дожидается всех операций.
     * @returns true — все операции успешны, область остаётся заблокированной до release;
     *          false — ошибка операции или flushPendingChanges (область уже разблокирована)
     *          либо завершение уже идёт
     */
    static async finish(scope) {
        const st = stateOf(scope);
        if (st.finishing)
            return false;
        st.finishing = true;
        let flushed = BgSaveScope.flushPendingChanges(scope);
        // Явный blur: inert сам по себе не обязан вызвать change у поля в фокусе
        const active = document.activeElement;
        if (active instanceof HTMLElement && scope.contains(active)) {
            active.blur();
        }
        flushed = BgSaveScope.flushPendingChanges(scope) && flushed;
        BgSaveScope.retryFailed(scope);
        scope.toggleAttribute("inert", true);
        scope.setAttribute("aria-busy", "true");
        // Очередь дожидаемся и при ошибке flush, чтобы область не разблокировалась во время записи
        let ok = false;
        try {
            ok = await BgSaveScope.waitIdle(scope) && flushed;
        }
        catch (e) {
            console.error("[BgSaveScope] wait failed", e);
        }
        st.failedFinish = !ok;
        if (!ok)
            BgSaveScope.release(scope);
        return ok;
    }
    /**
     * Блокирует область без ожидания очереди (finishing, inert, aria-busy);
     * снимается release. Повторный вызов безопасен.
     */
    static lock(scope) {
        stateOf(scope).finishing = true;
        scope.toggleAttribute("inert", true);
        scope.setAttribute("aria-busy", "true");
    }
    /** Снимает finishing и блокировку области */
    static release(scope) {
        const st = states.get(scope);
        if (st)
            st.finishing = false;
        scope.toggleAttribute("inert", false);
        scope.removeAttribute("aria-busy");
    }
}

export { BGSAVE_SCOPE_SELECTOR, BgSaveScope };
